/**
 * Loki.js
 * The Loki Enemy class
 */

import { Game } from "pixi_core";
import { BubbleColor, Enemies, EnemyAbilities, GameEvents, NB_MAX_ENEMIES } from "../Constants";
import { Enemy } from "./Enemy";

export class Loki extends Enemy {
    /**
     * @public
     * @param {String} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned) {
        super(enemyName, enemyData, index, summoned);

        const probAddRandomLine = Game.utils.assertNumber(enemyData.ProbAddRandomLine, 1);
        const probMimic = Game.utils.assertNumber(enemyData.ProbMimic, 1);
        const probIllusion = Game.utils.assertNumber(enemyData.ProbIllusion, 1);
        const probDecoy = Game.utils.assertNumber(enemyData.ProbDecoy, 1);

        // Ability choices
        this._abilityChoices.addChoice(EnemyAbilities.eADD_RANDOM_LINE, probAddRandomLine);
        this._abilityChoices.addChoice(EnemyAbilities.eMIMIC, probMimic);
        this._abilityChoices.addChoice(EnemyAbilities.eILLUSION, probIllusion);
        this._abilityChoices.addChoice(EnemyAbilities.eDECOY, probDecoy);

        /** @private @type {Number} - Number of bubbles to cast with the Mimic ability */
        this._nbBubblesMimic = Game.utils.assertNumber(enemyData.MimicNbr, 1);

        /** @private @type {Number} - Number of bubbles to cast with the Illusion ability */
        this._nbBubblesIllusion = Game.utils.assertNumber(enemyData.IllusionNbr, 1);
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     * @override
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0) {
                this._regularAttack();
            } else {
                // GDD: 100% chance to use duplicate skill if there is an empty slot in the enemy team
                const response = {};
                Game.fire(GameEvents.eREQUEST_ACTIVE_ENEMIES, response);

                if (response.activeEnemies.length < NB_MAX_ENEMIES) {
                    this._beginAbility(EnemyAbilities.eDUPLICATE);
                    return;
                }

                // Fallback to basic action choice
                super._chooseNextAction();
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * @protected
     * @override
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {
        switch (abilityName) {
            case EnemyAbilities.eADD_RANDOM_LINE:
                Game.fire(GameEvents.eGAME_ABILITY_ADD_RANDOM_LINE, this._nbAddedLines, startPos, this._onAbilityCompleted.bind(this));
                break;
            case EnemyAbilities.eMIMIC:
                let addBubbleDataMimic = {
                    nbBubbles: this._nbBubblesMimic,
                    color: BubbleColor.eMIMIC,
                    startPos: startPos,
                    dodgeFirst: false,
                    callback: this._onAbilityCompleted.bind(this)
                };
                Game.fire(GameEvents.eGAME_ABILITY_ADD_BUBBLE, addBubbleDataMimic);
                break;
            case EnemyAbilities.eILLUSION:
                let addBubbleDataIllusion = {
                    nbBubbles: this._nbBubblesIllusion,
                    color: BubbleColor.eCHAMELEON,
                    startPos: startPos,
                    dodgeFirst: false,
                    callback: this._onAbilityCompleted.bind(this)
                };
                Game.fire(GameEvents.eGAME_ABILITY_ADD_BUBBLE, addBubbleDataIllusion);
                break;
            case EnemyAbilities.eDECOY:
                Game.fire(
                    GameEvents.eGAME_ABILITY_TRANSFORM_TO_SHIELD,
                    this._nbBubblesDecoy,
                    startPos,
                    this._onAbilityCompleted.bind(this)
                );
                break;
            case EnemyAbilities.eDUPLICATE:
                Game.fire(GameEvents.eGAME_ABILITY_DUPLICATE, Enemies.eLOKI_COPY, this._onAbilityCompleted.bind(this));
                break;
            default:
                console.error("[Loki._useAbility] Invalid ability name : " + abilityName);
                this._chooseNextAction();
        }
    }
}
